import React, { useMemo, useState } from 'react';
import { Avatar, Box, Button, Divider, Grid, IconButton, Paper, Stack, TextField, Typography, Autocomplete } from '@mui/material';
import { Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material';
import Modal from '../../components/Modal';

function ComponentsOfProducts({ components, setComponents, onUpdate, currentProduct, availableComponents }) {

    const resolveImageUrl = (path) => {
        if (!path) return null;

        const normalized = `${path}`.replace(/^\/+/, '');
        if (normalized.startsWith('http')) {
            return normalized;
        }

        return `${process.env.REACT_APP_DATA_PIC_PATH}/${normalized}`;
    };

    const resolveComponentId = (componentEntry) => {
        if (!componentEntry) return '';

        const reference =
            typeof componentEntry === 'object' && (componentEntry.product || componentEntry.component)
                ? componentEntry.product ?? componentEntry.component
                : componentEntry;

        if (!reference) return '';
        if (typeof reference === 'string') return reference;
        if (typeof reference === 'object') {
            return (
                reference._id ||
                reference.id ||
                reference.value ||
                reference.product ||
                reference.component ||
                ''
            );
        }
        return '';
    };

    const componentOptions = useMemo(() => {
        const source = Array.isArray(availableComponents) ? availableComponents : [];
        return source
            .map((component) => {
                const value = component?._id || component?.id || component?.product || component?.component;
                const label = component?.name || component?.label || 'Produit';
                const imageCandidate = component?.imageUrl || component?.url_product || component?.picture || component?.image;

                return {
                    value,
                    label,
                    imageUrl: resolveImageUrl(imageCandidate),
                    regular_price: component?.regular_price ?? component?.price ?? component?.discounted_price ?? 0,
                    price: component?.price,
                    discounted_price: component?.discounted_price,
                };
            })
            .filter((option) => option.value && option.label);
    }, [availableComponents]);

    const [searchTerm, setSearchTerm] = useState('');

    const priceFormatter = useMemo(() => {
        try {
            return new Intl.NumberFormat('fr-FR', {
                style: 'currency',
                currency: 'MAD',
                minimumFractionDigits: 2,
            });
        } catch (error) {
            return null;
        }
    }, []);

    const componentLookup = useMemo(() => {
        return componentOptions.reduce((acc, option) => {
            acc[option.value] = option;
            return acc;
        }, {});
    }, [componentOptions]);

    const resolvedComponents = useMemo(() => {
        return components
            .map((comp) => {
                const id = resolveComponentId(comp);
                const detail = componentLookup[id];
                const quantity = parseFloat(comp.quantity) || 0;
                const unitPrice = parseFloat(
                    detail?.regular_price ?? detail?.price ?? detail?.discounted_price ?? 0
                ) || 0;

                return {
                    id,
                    quantity,
                    unitPrice,
                    total: quantity * unitPrice,
                    label: detail?.label,
                    imageUrl: detail?.imageUrl,
                };
            })
            .filter((entry) => entry.id && entry.quantity > 0);
    }, [components, componentLookup]);

    const grandTotal = useMemo(
        () => resolvedComponents.reduce((sum, comp) => sum + comp.total, 0),
        [resolvedComponents]
    );

    const handleChange = (index, field, value) => {
        const updated = [...components];
        if (!updated[index]) {
            updated[index] = { product: '', quantity: '' };
        }
        updated[index][field] = value;
        setComponents(updated);
    };

    const handleAdd = () => {
        setComponents([...components, { product: '', quantity: '1' }]);
    };

    const handleRemove = (index) => {
        const updated = components.filter((_, i) => i !== index);
        setComponents(updated);
    };

    return (
        <>
        <style>{`
            #componentOfProducts {
                z-index: 9999 !important;
            }
            #componentOfProducts ~ .modal-backdrop {
                z-index: 9998 !important;
            }
        `}</style>
        <Modal
            modalId="componentOfProducts"
            title={`Composant des Produits "${(currentProduct?.name || '').substring(0, 30)}"`}
            modalType="modal-xxl"
            submitLabel="Mettre à jour"
            onSubmit={onUpdate}
        >
            <Box sx={{ p: 2 }}>
                <Typography variant="h5" sx={{ mb: 2, color: 'primary.main', fontWeight: 700 }}>
                    {currentProduct?.name || 'Composants du produit'}
                </Typography>

                <Grid container spacing={3}>
                    <Grid item xs={12} md={7} lg={8}>
                        {(!availableComponents || availableComponents.length === 0) && (
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Aucun composant disponible. Marquez d'autres produits comme composants pour les sélectionner ici.
                            </Typography>
                        )}

                        <TextField
                            fullWidth
                            label="Rechercher un composant"
                            placeholder="Chercher par nom"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            variant="outlined"
                            sx={{ mb: 3, backgroundColor: 'rgba(63, 81, 181, 0.06)' }}
                        />

                        {components.map((comp, index) => (
                            <Paper
                                key={index}
                                elevation={3}
                                sx={{
                                    p: 2,
                                    mb: 2,
                                    borderRadius: 2,
                                    background: 'linear-gradient(135deg, #f3e5f5 0%, #e3f2fd 100%)',
                                }}
                            >
                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
                                    <TextField
                                        type="number"
                                        label="Quantité"
                                        value={comp.quantity}
                                        onChange={(e) => handleChange(index, 'quantity', e.target.value)}
                                        InputProps={{ inputProps: { min: 0 } }}
                                        sx={{ minWidth: 120 }}
                                    />

                                    <Box flex={1} minWidth={250}>
                                        <Autocomplete
                                            options={componentOptions}
                                            filterOptions={(options, state) => {
                                                const externalTerm = searchTerm.trim().toLowerCase();
                                                const inputTerm = state.inputValue ? state.inputValue.toLowerCase() : '';

                                                return options.filter((option) => {
                                                    const label = option.label.toLowerCase();
                                                    const matchesSearch = !externalTerm || label.includes(externalTerm);
                                                    const matchesInput = !inputTerm || label.includes(inputTerm);
                                                    return matchesSearch && matchesInput;
                                                });
                                            }}
                                            isOptionEqualToValue={(option, value) => option.value === value.value}
                                            getOptionLabel={(option) => option.label}
                                            value={componentOptions.find((o) => o.value === resolveComponentId(comp)) || null}
                                            onChange={(_, option) => handleChange(index, 'product', option ? option.value : '')}
                                            renderInput={(params) => (
                                                <TextField {...params} label="Composant" placeholder="Sélectionner un produit" />
                                            )}
                                            renderOption={(props, option) => (
                                                <li {...props}>
                                                    <Stack direction="row" spacing={2} alignItems="center">
                                                        <Avatar src={option.imageUrl} alt={option.label} sx={{ bgcolor: 'primary.main' }}>
                                                            {option.label.charAt(0)}
                                                        </Avatar>
                                                        <Box>
                                                            <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                                                {option.label}
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary">
                                                                #{option.value}
                                                            </Typography>
                                                        </Box>
                                                    </Stack>
                                                </li>
                                            )}
                                        />
                                    </Box>

                                    <IconButton color="error" onClick={() => handleRemove(index)} aria-label="Supprimer le composant">
                                        <DeleteIcon />
                                    </IconButton>
                                </Stack>
                            </Paper>
                        ))}

                        <Button
                            variant="contained"
                            color="secondary"
                            startIcon={<AddIcon />}
                            onClick={handleAdd}
                            sx={{ mt: 2, borderRadius: 2, boxShadow: 2 }}
                        >
                            Ajouter un composant
                        </Button>
                    </Grid>

                    <Grid item xs={12} md={5} lg={4}>
                        <Paper elevation={4} sx={{ p: 3, borderRadius: 3, backgroundColor: '#fdf7ff' }}>
                            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                                Récapitulatif de la composition
                            </Typography>
                            <Divider sx={{ mb: 2 }} />

                            {resolvedComponents.length === 0 && (
                                <Typography variant="body2" color="text.secondary">
                                    Ajoutez des composants pour voir le récapitulatif.
                                </Typography>
                            )}

                            <Stack spacing={2} divider={<Divider />}> 
                                {resolvedComponents.map((item) => (
                                    <Stack key={item.id} direction="row" spacing={2} alignItems="center">
                                        <Avatar src={item.imageUrl} alt={item.label} sx={{ bgcolor: 'secondary.main', width: 48, height: 48 }}>
                                            {item.label?.charAt(0)}
                                        </Avatar>
                                        <Box flex={1}>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                                {item.label || 'Composant'}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Qté: {item.quantity}
                                            </Typography>
                                        </Box>
                                        <Box textAlign="right">
                                            <Typography variant="body2" color="text.secondary">
                                                {priceFormatter?.format ? priceFormatter.format(item.unitPrice || 0) : `${item.unitPrice || 0} MAD`}
                                            </Typography>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                                {priceFormatter?.format ? priceFormatter.format(item.total || 0) : `${item.total || 0} MAD`}
                                            </Typography>
                                        </Box>
                                    </Stack>
                                ))}
                            </Stack>

                            <Divider sx={{ my: 2 }} />
                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                    Total
                                </Typography>
                                <Typography variant="h6" sx={{ fontWeight: 800, color: 'primary.main' }}>
                                    {priceFormatter?.format ? priceFormatter.format(grandTotal) : `${grandTotal} MAD`}
                                </Typography>
                            </Stack>
                        </Paper>
                    </Grid>
                </Grid>
            </Box>
        </Modal>
        </>
    );
}

export default ComponentsOfProducts;
